import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { updateJob } from "@endpoints/job.service";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { RootState } from "@store/reducers";
import { getHiringCompanyWithJobCount } from "@endpoints/recruiter.service";
import AnimatedNumber from "@components/AnimatedNumber";
import { NavLink } from "react-router-dom";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { SearchIcon } from "lucide-react";

const CompanyRowCard = ({ data }) => {
    return (
        <NavLink
            to={`/job/list-by-company/${data?.hiringCompany?._id}`}
            className="grid grid-cols-[1.1fr_1.4fr_0.4fr] gap-16 items-center justify-between bg-white border-b border-[#ececec] rounded px-4 py-5 hover:bg-gray-50 cursor-pointer"
        >
            <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                    <span className="text-[10px] text-light-black font-medium pl-2">
                        {data?.hiringCompany?.refNo || "-"}
                    </span>
                </div>
                <div className={`flex gap-4 rounded-md py-2.5 px-5 border border-y-gray-100 border-r-gray-100 border-green-600 border-l-[3px]`}>
                    <div className="flex items-center justify-center w-[35px] h-[35px] bg-gray-100 rounded-full">
                        {
                            data?.hiringCompany?.profilePic ?
                                <img src={data?.hiringCompany?.profilePic} className="w-full h-full object-cover rounded-full" />
                                :
                                <span className="font-bold text-charcoal">{data?.hiringCompany?.companyName?.charAt(0)}</span>
                        }
                    </div>
                    <div className="flex flex-col gap-0.5 text-charcoal">
                        <span className="text-xs font-bold line-clamp-1">{data?.hiringCompany?.companyName}</span>
                        <span className="text-xss font-medium">{data?.hiringCompany?.jobTitle || "London, UK"}</span>
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-1">
                <span className="font-medium text-xss italic text-light-black">{dayjs(data?.latestTracker?.tracker?.createdAt).format('hh:mm a Do MMM YY')}</span>
                <div className="flex flex-col justify-center border rounded-md p-3 h-[3.8rem]">
                    <span className="text-xs text-light-black font-medium">Latest Activity : <span className="font-bold text-charcoal">{data?.latestTracker?.tracker?.status || 'N/A'}</span></span>
                </div>
            </div>
            <div className="flex flex-col items-center text-xss gap-2">
                Open Jobs
                <div className="h-[53px] w-[53px] rounded-full border-[3.5px] flex">
                    <AnimatedNumber
                        value={data?.totalJobs || 0}
                        className="font-bold text-sm text-charcoal m-auto"
                    />
                </div>
            </div>
        </NavLink>
    );
};

const LIMIT = 25
const PAGE_LIMIT = 5

const ViewByHiringCompany = () => {
    const hasFetched = useRef(false);
    const sentinelRef = useRef(null)
    const scrollListRef = useRef(null)
    const { enqueueSnackbar } = useSnackbar();
    const {
        control,
        formState: { errors },
        watch,
    } = useForm({
        defaultValues: {
            search: undefined,
            searchBy: "Search by",
        },
    });
    const fields = watch();

    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [hiringCompanies, setHiringCompanies] = useState([])
    const [page, setPage] = useState(1)
    const [hasMoreData, setHasMoreData] = useState(false)


    async function fetchHiringCompanies() {
        try {
            if (page > 1) {
                setIsLoadingMore(true)
            } else {
                setIsLoading(true)
            }
            const { success, data, pagination } = await getHiringCompanyWithJobCount({ limit: LIMIT, page })
            if (success) {
                setHiringCompanies((prev) => page === 1 ? data : [...prev, ...data])
                setHasMoreData(Boolean(pagination?.hasMore))
            }
        } catch (error) {
            enqueueSnackbar(error, { variant: 'error' })
        } finally {
            setIsLoading(false)
            setIsLoadingMore(false)
        }
    }

    function handleLoadMore() {
        if (!hasMoreData) return
        setPage((prev) => prev + 1)
    }


    // const fetchHiringCompanies2 = () => {
    //     getHiringCompanyWithJobCount({ limit: 100, page: 1 })
    //         .then((response) => {
    //             const { data, success } = response;
    //             if (success) {
    //                 setHiringCompanies(data)
    //             }
    //         })
    //         .finally(() => setIsLoading(false));
    // };

    const handleCloseJob = async (jobId, status) => {
        try {
            const { success } = await updateJob({ jobId, status });
            if (success) {
                fetchHiringCompanies();
                enqueueSnackbar("Job Closed Successfully!", { variant: "success" });
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (!sentinelRef?.current || !scrollListRef?.current) return
        const observer = new IntersectionObserver((entries) => {
            if (entries?.[0]?.isIntersecting) {
                handleLoadMore()
            }
        },
            {
                root: scrollListRef?.current,
                rootMargin: '0px',
                threshold: 1
            }
        )

        observer.observe(sentinelRef?.current)
        return () => {
            observer.disconnect()
        };
    }, [isLoading, isLoadingMore])

    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true;
            fetchHiringCompanies();
        }
    }, [fields?.search]);

    return (
        <div className="min-h-0 flex flex-col flex-1">
            {/* Search container */}
            <div className="flex items-center w-max my-3.5 border rounded-md bg-gray-50 px-3">
                <input
                    className="w-64 text-xs font-medium bg-transparent outline-none h-9"
                    placeholder="Search..."
                />
                <SearchIcon color="gray" size={16} />
            </div>
            {/* List section  */}
            {
                isLoading ?
                    <div className="flex flex-1 justify-center items-center">
                        <LoadingAnimation color="black" />
                    </div>
                    :
                    !hiringCompanies?.length ?
                        <span className="m-auto font-medium text-sm text-light-black">No hiring companies available.</span>
                        :
                        <div
                            ref={scrollListRef}
                            className="flex flex-col min-h-0 overflow-y-scroll no-scrollbar border-t">
                            {hiringCompanies?.map((item) => {
                                return (
                                    <CompanyRowCard
                                        data={item}
                                    />
                                );
                            })}
                            {isLoadingMore ?
                                <div className="py-2 mx-auto font-medium text-light-black text-sm flex items-center gap-2"><LoadingAnimation color="black" /> Loading more data</div>
                                :
                                null
                            }
                            {/* Sentinel */}
                            <div ref={sentinelRef} className="h-px" />
                        </div>
            }
        </div>
    );

};

export default ViewByHiringCompany;
